import type {
  AnalyticsQuery,
  AnalyticsQueryFilters,
} from "schemas/generated/analyticsRead";

import { minutesToMilliseconds } from "date-fns";
import { currencySchema } from "schemas/project";
import { z } from "zod";

export const DEFAULT_MAIN_RANGE_ID = "f13ffb7f-ea60-474c-a3dd-669070b5f8a2";
export const DEFAULT_COMPARE_AT_RANGE_ID =
  "a24a44db-3d1b-4ad7-a7c5-de1da66cad95";

export const metricNameSchema = z
  .enum([
    "conversions",
    "views",
    "unique_sessions",
    "conversion_rates",
    "revenue",
    "revenue_per_session",
    "average_order_value",
  ])
  .describe("MetricName");

/**
 * --------------------------------
 * Schemas for AnalyticsResponse
 * --------------------------------
 */

const rangeResultMetadataSchema = z.object({
  elementId: z.uuid().nullable(),
  title: z.string(),
  url: z.string(),
});

const rangeResultMetricsSchema = z.record(
  metricNameSchema,
  z.array(z.number()),
);

export const rangeResultSchema = z
  .object({
    urlPath: z.string(),
    metadata: rangeResultMetadataSchema,
    rangeId: z.uuid(),
    metrics: rangeResultMetricsSchema,
  })
  .describe("RangeResult");

const totalRowsCountSchema = z.number().min(0);

export const analyticsResponseSchema = z
  .object({
    totalRowsCount: totalRowsCountSchema,
    rangeResults: z.array(rangeResultSchema),
  })
  .describe("AnalyticsResponse");

/**
 * --------------------------------
 * Schemas for AnalyticsQuery
 * --------------------------------
 */

const urlHostsSchema = z.array(z.string());
const offsetSchema = z.number().min(0);
const limitSchema = z.number().positive().max(100);

export const orderSchema = z.enum(["ASC", "DESC"]).describe("Order");

export const pageTypeSchema = z
  .enum(["allPages", "entryPages"])
  .describe("PageType");

export const conditionOperatorSchema = z
  .enum(["equals", "does_not_equal", "contains", "does_not_contain"])
  .describe("ConditionOperator");

export const filterConditionSchema = z
  .discriminatedUnion("operator", [
    z.object({
      operator: z.literal("equals"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z.literal("does_not_equal"),
      value: z.array(z.string()),
    }),
    z.object({
      operator: z.literal("contains"),
      value: z.string(),
    }),
    z.object({
      operator: z.literal("does_not_contain"),
      value: z.string(),
    }),
  ])
  .describe("FilterCondition");

export const filterConditionsSchema = z
  .array(filterConditionSchema)
  .describe("FilterConditions");

export const urlParamsSchema = z
  .record(z.string(), filterConditionsSchema)
  .describe("QueryFiltersUrlParams");

export const filterSchema = z
  .object({
    urlPath: filterConditionsSchema,
    pageType: z.enum(["allPages", "entryPages"]).describe("PageType"),
    urlParams: urlParamsSchema,
  })
  .describe("AnalyticsQueryFilters");

export const queryRangeSchema = z
  .object({
    id: z.uuid(),
    startDatetime: z.number().min(0),
    endDatetime: z.number().positive(),
    interval: z.number().min(10),
  })
  .describe("QueryRange");

export const adjustedRangeSchema = queryRangeSchema
  .superRefine((input, ctx) => {
    const startDatetime = input.startDatetime;
    const endDatetime = input.endDatetime;
    const interval = input.interval;

    if (endDatetime <= startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "range.endDatetime must be after range.startDatetime",
        path: ["sort", "endDatetime"],
      });
    }

    if (interval > endDatetime - startDatetime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval time must be shorter than the time between range.startDatetime and range.endDatetime",
        path: ["range", "interval"],
      });
    }

    if ((endDatetime - startDatetime) % interval !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Interval doesn't fit into the range: range.endDatetime must be aligned with (i.e. a multiple of) the interval from range.startDatetime",
        path: ["range", "interval"],
      });
    }

    if (interval % minutesToMilliseconds(10) !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Interval must be a multiple of 10 minutes",
        path: ["range", "interval"],
      });
    }
  })
  .describe("AdjustedRange");

export const queryRangesSchema = z
  .object({
    mainRange: queryRangeSchema,
    compareAtRanges: z.array(queryRangeSchema),
  })
  .describe("QueryRanges");

export const analyticsQuerySchema = z
  .object({
    /**
     * Workspace is always required. This is how we
     * check the individual paths to be sure we're looking up the correct ones for the workspace.
     */
    urlHosts: urlHostsSchema.optional(),
    /**
     * The metrics that are directly stored in ClickHouse's timeseries_records table, and/or metrics
     * that need to be calculated from metrics that are stored in ClickHouse. E.g. "conversions",
     * "views", "conversion_rates".
     */
    metrics: z.array(metricNameSchema).min(1),
    /**
     * Sorting order for the sorting metric.
     */
    order: orderSchema,
    /**
     * Sort based on a given metric name.
     */
    sortMetric: metricNameSchema,
    /**
     * The range being considered, including the interval (in MINUTES) of the buckets that we want to retrieve. E.g. if interval = 30, then we're
     * aggregating the metric calculation in buckets of 30min.
     */
    ranges: queryRangesSchema,
    /**
     * Offset used in pagination.
     */
    offset: offsetSchema,
    /**
     * Limit of the results per page.
     */
    limit: limitSchema,
    filters: filterSchema,
    targetCurrency: currencySchema.nullish(),
    mode: z
      .enum(["groupByUrlPath", "groupByDomain", "funnel"])
      .prefault("groupByUrlPath"),
    isFractional: z.boolean().prefault(false),
  })
  .superRefine((input, ctx) => {
    if (!input.metrics.includes(input.sortMetric)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Sort value must be one of of the specified metrics.",
        path: ["sort"],
      });
    }
  })
  .describe("AnalyticsQuery");

export type AnalyticsQueryWithoutRanges = Omit<AnalyticsQuery, "ranges">;

/**
 * NOTE (Max, 2024-09-23): Filters that are used in sql.ts to filter out individual
 * events, regardless of the pageType (entryPages | allPages). We're not including
 * pageType as this one is used to alter the construction of CTEs, whereas the
 * SQL WHERE clause for the other filters remains the same, regardless of pageType.
 */
export type QueryFiltersRoot = Pick<AnalyticsQueryFilters, "urlPath">;

/**
 * --------------------------------
 * Schemas for retrieving urlParams
 * --------------------------------
 */

export const findUrlParamsQuerySchema = z
  .object({
    urlHosts: urlHostsSchema,
    order: orderSchema,
    offset: offsetSchema,
    limit: limitSchema,
    key: z.string(),
    value: z.string(),
  })
  .describe("FindUrlParamsQuery");

export const findUrlParamsResponseSchema = z
  .object({
    urlParams: z.array(z.string()),
  })
  .describe("FindUrlParamsResponse");

export const findSubDomainsForUrlHostsResponseSchema = z
  .array(
    z.object({
      urlHost: z.string(),
      isRootDomain: z.boolean(),
    }),
  )
  .describe("FindSubDomainsForUrlHostsResponse");

/**
 * --------------------------------
 * Public analytics SQL query contract
 * --------------------------------
 */

type AnalyticsQueryColumn = {
  name: string;
  type: string;
  public: boolean;
};

type AnalyticsQueryTable = {
  name: string;
  description: string;
  projectScoped: boolean;
  public: boolean;
  /** Column each row is dated by (`date` on the event tables, `day` on the rollups), or null for tables with no time series. */
  timestampColumn: string | null;
  columns: AnalyticsQueryColumn[];
};

export const analyticsQueryLimits = {
  maxExecutionSeconds: 60,
  maxResultRows: 100_000,
} as const;

export const analyticsQueryGuidance = [
  "Use SELECT or WITH queries only.",
  "Do not add project or namespace filters; project scoping is enforced server-side.",
  "Prefer daily rollup tables for date-range summaries and use events_computed for ad-hoc session-level questions.",
] as const;

export const analyticsQueryTables = [
  {
    name: "events_computed",
    description:
      "Session-level computed analytics. Primary table for ad-hoc queries.",
    projectScoped: true,
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "subject", type: "UUID", public: true },
      { name: "date", type: "DateTime64(3)", public: true },
      { name: "id", type: "UUID", public: false },
      { name: "name", type: "LowCardinality(String)", public: true },
      { name: "data", type: "String", public: false },
      { name: "computed", type: "String", public: false },
      { name: "computedVersion", type: "UInt8", public: false },
      { name: "computedDate", type: "DateTime64(3)", public: false },
      { name: "insertDate", type: "DateTime64(3)", public: false },
      { name: "path", type: "String", public: true },
      { name: "domain", type: "String", public: true },
      { name: "currencyCode", type: "String", public: true },
      { name: "subjectPurchaseSum", type: "Float64", public: true },
      { name: "subjectPurchaseCount", type: "Float64", public: true },
      { name: "subjectPurchaseFraction", type: "Float64", public: true },
      { name: "subjectPurchaseFractionalSum", type: "Float64", public: true },
      { name: "title", type: "String", public: true },
    ],
  },
  {
    name: "namespace_to_domain",
    description: "Maps project analytics namespaces to domains.",
    projectScoped: true,
    public: false,
    timestampColumn: null,
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "domain", type: "String", public: false },
    ],
  },
  {
    name: "currency_exchange_rates",
    description: "Currency exchange rates for multi-currency reporting.",
    projectScoped: false,
    public: true,
    timestampColumn: "date",
    columns: [
      { name: "date", type: "Date", public: true },
      { name: "code", type: "String", public: true },
      { name: "rate", type: "Float32", public: true },
    ],
  },
  {
    name: "daily_page_rollups",
    description:
      "Pre-aggregated daily page metrics by day, domain, path, and UTM source.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "domain", type: "String", public: true },
      { name: "path", type: "String", public: true },
      { name: "utm_source", type: "String", public: true },
      { name: "views_state", type: "AggregateFunction(count)", public: true },
      {
        name: "unique_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "converting_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "fractional_conversions_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "fractional_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "session_conversions_state",
        type: "AggregateFunction(sum, UInt64)",
        public: true,
      },
      {
        name: "session_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
    ],
  },
  {
    name: "daily_namespace_rollups",
    description:
      "Pre-aggregated daily project traffic metrics by day and UTM source.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "utm_source", type: "String", public: true },
      { name: "views_state", type: "AggregateFunction(count)", public: true },
      {
        name: "unique_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
      {
        name: "converting_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
    ],
  },
  {
    name: "daily_namespace_purchase_rollups",
    description: "Pre-aggregated daily purchase metrics by day and UTM source.",
    projectScoped: true,
    public: true,
    timestampColumn: "day",
    columns: [
      { name: "day", type: "Date", public: true },
      { name: "namespace", type: "String", public: false },
      { name: "utm_source", type: "String", public: true },
      {
        name: "purchase_count_state",
        type: "AggregateFunction(count)",
        public: true,
      },
      {
        name: "total_revenue_usd_state",
        type: "AggregateFunction(sum, Float64)",
        public: true,
      },
      {
        name: "unique_purchasing_sessions_state",
        type: "AggregateFunction(uniq, UUID)",
        public: true,
      },
    ],
  },
  {
    name: "consent_events",
    description:
      "Append-only visitor consent decisions for audits and exports.",
    projectScoped: true,
    public: false,
    timestampColumn: "date",
    columns: [
      { name: "namespace", type: "String", public: false },
      { name: "consent_id", type: "String", public: false },
      { name: "date", type: "DateTime64(3)", public: false },
      { name: "id", type: "UUID", public: false },
      { name: "action", type: "String", public: false },
      { name: "consent_mode", type: "String", public: false },
      { name: "policy_version", type: "UInt32", public: false },
      { name: "categories", type: "String", public: false },
      { name: "region", type: "String", public: false },
      { name: "gpc", type: "UInt8", public: false },
      { name: "useragent", type: "String", public: false },
      { name: "referrer", type: "String", public: false },
      { name: "ip_country", type: "String", public: false },
    ],
  },
] as const satisfies readonly AnalyticsQueryTable[];

export const publicAnalyticsQueryTables = analyticsQueryTables
  .filter((table) => table.public)
  .map((table) => {
    return {
      ...table,
      columns: table.columns.filter((column) => column.public),
    };
  });
